import { useEffect, useRef } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LocationResultList } from '@/components/location-result-list'
import { MonitorSiteCta } from '@/components/monitor-site-cta'
import { WebsiteCheckVerdict } from '@/components/website-check-verdict'
import { useWebsiteCheck } from '@/hooks/use-website-check'
import { rowsOf } from '@/lib/website-check-stream'
import { isAutomatedVisitor } from '@/lib/automated-visitor'
import { claimAutoCheck, markChecked } from '@/lib/auto-check-guard'
import { downPageAnswer, downPageFaq, downPageHead, downPageStatusNote } from '@/lib/down-page'

export const Route = createFileRoute('/_site/tools/is-website-down_/$domain')({
  component: IsItDownRoute,
  // The list loads lazily (its own chunk), never in the main bundle.
  loader: async ({ params }) => {
    const { downSite, relatedSites } = await import('@/lib/down-sites')
    return { site: downSite(params.domain) ?? null, related: relatedSites(params.domain) }
  },
  head: ({ params, loaderData }) => downPageHead(params.domain, loaderData?.site ?? undefined),
})

function IsItDownRoute() {
  const { domain } = Route.useParams()
  // A new domain starts with a clean page, never with the previous service's results.
  return <IsItDownPage key={domain} domain={domain} />
}

function IsItDownPage({ domain }: { domain: string }) {
  const { site, related } = Route.useLoaderData()
  const name = site?.name ?? domain
  const faq = downPageFaq(name, domain, site?.statusPage)

  const { run, check } = useWebsiteCheck()

  // Real visitors get a live check on load. Crawlers and headless browsers do not: each visit
  // is a 24-location probe. A browser tab also auto-checks the same domain at most once a
  // minute; a reload inside that minute shows the "Check now" button instead.
  const started = useRef(false)
  useEffect(() => {
    if (isAutomatedVisitor()) return
    // `started` keeps a development re-mount (StrictMode) from losing the check it already claimed.
    if (started.current || claimAutoCheck(domain)) {
      started.current = true
      check(domain)
    }
  }, [domain, check])

  const checkNow = () => {
    markChecked(domain)
    check(domain)
  }

  const rateLimited = run.status === 'error' && run.errorStatus === 429
  const showResults = run.status === 'running' || run.status === 'done'

  return (
    <>
      <section className="container mx-auto px-4 pb-8 pt-12 md:pt-16">
        <div className="max-w-3xl">
          <p className="mb-3 text-sm text-muted-foreground">{site?.category ?? 'Website'}</p>

          <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Is {name} down right now?</h1>

          {/* The answer in plain HTML: a crawler gets it without the live check */}
          <p className="mt-4 max-w-2xl text-lead text-body">{downPageAnswer(name, domain)}</p>

          {/* Live status: progress while locations answer, then the verdict */}
          <WebsiteCheckVerdict run={run} name={name} className="mt-6" />

          {run.status === 'idle' && (
            <Button className="mt-6" onClick={checkNow}>
              Check {name} now
            </Button>
          )}

          {run.status === 'error' && (
            <div role="status" className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
              <span className="text-sm text-body">
                {rateLimited ? 'Too many checks right now. Try again in a minute.' : 'The check could not run. Try again.'}
              </span>
              <Button variant="outline" size="sm" onClick={checkNow}>
                Try again
              </Button>
            </div>
          )}

          {site && <p className="mt-6 max-w-2xl text-lead text-body">{site.description}</p>}

          {site?.statusPage && (
            <div className="mt-8 max-w-2xl">
              <h2 className="text-xl font-semibold">{name} status page</h2>
              <p className="mt-2 text-body">{downPageStatusNote(name, domain, site.statusPage)}</p>
              <a
                href={site.statusPage}
                target="_blank"
                rel="nofollow noopener"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                Open the official {name} status page
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Per-location results, filled in as each location answers */}
      {showResults && (
        <section className="pb-12">
          <div className="container mx-auto px-4">
            {run.note && <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{run.note}</p>}
            <LocationResultList results={rowsOf(run)} />

            {run.status === 'done' && <MonitorSiteCta url={run.url ?? `https://${domain}`} className="mt-8" />}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button variant="outline" onClick={checkNow} disabled={run.status === 'running'}>
                Check again
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* The same questions as the FAQPage JSON-LD */}
      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <h2 className="mb-6 text-xl font-semibold">Common questions</h2>
          <div className="max-w-3xl divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <div key={f.question} className="py-5">
                <h3 className="mb-2 font-semibold text-foreground">{f.question}</h3>
                <p className="leading-relaxed text-body">{f.answer}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-3xl text-body">
            To get an alert instead of checking by hand, read{' '}
            <Link to="/blog/what-is-uptime-monitoring" className="underline">
              what uptime monitoring is
            </Link>
            , see{' '}
            <Link to="/features/uptime-monitoring" className="underline">
              how Uptrack monitors a site
            </Link>{' '}
            or compare the{' '}
            <Link to="/pricing" className="underline">
              plans
            </Link>
            .
          </p>
        </div>
      </section>

      {/* More services: the same category first */}
      <section className="border-t border-border bg-card">
        <div className="container mx-auto px-4 py-12">
          <h2 className="mb-6 text-xl font-semibold">Check other services</h2>
          <div className="flex max-w-3xl flex-wrap gap-2">
            {related.map((s) => (
              <Link
                key={s.domain}
                to="/tools/is-website-down/$domain"
                params={{ domain: s.domain }}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm transition-colors hover:border-primary"
              >
                {s.name}
              </Link>
            ))}
            <Link
              to="/tools/is-website-down"
              hash="services"
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-primary"
            >
              All services
            </Link>
            <Link
              to="/tools/is-website-down"
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-primary"
            >
              Check any site →
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
